import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadStoredDocument } from '@/helpers/downloadFile';
import downloadIcon from '@/assets/icons/download-file.svg';
import styles from './DownloadFileButton.module.css';

interface DownloadFileButtonProps {
  /** The file's secure download link - the `uri` (or `url`) field of a document DTO. */
  uri?: string | null;
  /** Name to save the file under; the document's display name. */
  fileName?: string | null;
  /** Extra classes, for lists that need a different size or spacing. */
  className?: string;
  /** A different glyph, for surfaces whose colours the default dark icon does not suit. */
  iconSrc?: string;
}

/**
 * Download icon shown next to a file in a list, so a file can be saved without opening it first.
 * Rendered as a button rather than a link because the bytes come back through the API (see
 * downloadStoredDocument); if that round-trip fails we fall back to opening the file in a new tab -
 * what these lists did before this button existed - so the user is never left with a dead control.
 */
const DownloadFileButton = ({ uri, fileName, className, iconSrc }: DownloadFileButtonProps) => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);

  if (!uri) return null;

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    // These buttons sit inside clickable rows and inside <label>s - neither should react.
    event.preventDefault();
    event.stopPropagation();
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      await downloadStoredDocument(uri, fileName?.trim() || t('downloadFile.fallbackFileName'));
    } catch (error) {
      console.error('Failed to download the file, opening it in a new tab instead.', error);
      window.open(uri, '_blank', 'noopener,noreferrer');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <button
      type="button"
      title={t('downloadFile.title')}
      aria-label={t('downloadFile.title')}
      onClick={handleClick}
      disabled={isDownloading}
      className={`${styles.button} ${className ?? ''}`}
    >
      {isDownloading ? <span className={styles.spinner} /> : <img src={iconSrc ?? downloadIcon} alt="" className={styles.icon} />}
    </button>
  );
};

export default DownloadFileButton;
